/** Vorschläge für die Autovervollständigung im Composer (@ Mitglieder/Rollen, # Kanäle, : Emojis). */
import { isTextBased, isThread, type ChannelDto, type EmojiDto, type RoleDto, type UserDto } from '@lagerfeuer/shared';
import type { PickedEmoji, UnicodeEmoji } from '../../components/EmojiPicker';
import type { Trigger } from './composerText';

export type Suggestion =
  | { kind: 'user'; key: string; id: string; label: string; sub: string; user: UserDto; insert: string; raw: string }
  | { kind: 'role'; key: string; id: string; label: string; color: string | null; insert: string; raw: string }
  | { kind: 'everyone'; key: string; label: string; sub: string; insert: string; raw: string }
  | { kind: 'channel'; key: string; id: string; label: string; channel: ChannelDto; insert: string; raw: string }
  | { kind: 'emoji'; key: string; label: string; emoji: PickedEmoji; insert: string; raw: string };

export interface SuggestSource {
  /** Kandidaten für @ (Mitglieder bzw. DM-Teilnehmer) mit Anzeigename. */
  people: { user: UserDto; name: string }[];
  roles: RoleDto[];
  canMentionEveryone: boolean;
  channels: ChannelDto[];
  customEmojis: EmojiDto[];
  unicode: (q: string, limit: number) => UnicodeEmoji[];
  roleColor: (r: RoleDto) => string | null;
}

const LIMIT = 10;

function score(hay: string[], q: string): number {
  if (!q) return 1;
  let best = 0;
  for (const h of hay) {
    const s = h.toLowerCase();
    if (s === q) best = Math.max(best, 4);
    else if (s.startsWith(q)) best = Math.max(best, 3);
    else if (s.split(/[\s_.-]+/).some((w) => w.startsWith(q))) best = Math.max(best, 2);
    else if (s.includes(q)) best = Math.max(best, 1);
  }
  return best;
}

export function suggest(t: Trigger, src: SuggestSource): Suggestion[] {
  const q = t.query.toLowerCase();
  if (t.kind === '@') {
    const out: { s: Suggestion; score: number }[] = [];
    for (const p of src.people) {
      const sc = score([p.name, p.user.username, p.user.displayName ?? ''], q);
      if (sc) {
        out.push({
          score: sc + 0.5,
          s: { kind: 'user', key: `u${p.user.id}`, id: p.user.id, label: p.name, sub: p.user.username, user: p.user, insert: `@${p.name}`, raw: `<@${p.user.id}>` },
        });
      }
    }
    for (const r of src.roles) {
      if (r.isEveryone || (!r.mentionable && !src.canMentionEveryone)) continue;
      const sc = score([r.name], q);
      if (sc) out.push({ score: sc, s: { kind: 'role', key: `r${r.id}`, id: r.id, label: r.name, color: src.roleColor(r), insert: `@${r.name}`, raw: `<@&${r.id}>` } });
    }
    if (src.canMentionEveryone) {
      for (const [w, sub] of [
        ['everyone', 'Benachrichtigt alle mit Zugriff auf den Kanal'],
        ['here', 'Benachrichtigt alle, die gerade online sind'],
      ] as const) {
        const sc = score([w], q);
        if (sc) out.push({ score: sc, s: { kind: 'everyone', key: w, label: `@${w}`, sub, insert: `@${w}`, raw: `@${w}` } });
      }
    }
    return out
      .sort((a, b) => b.score - a.score || a.s.label.localeCompare(b.s.label, 'de'))
      .slice(0, LIMIT)
      .map((x) => x.s);
  }
  if (t.kind === '#') {
    return src.channels
      .filter((c) => c.type !== 'CATEGORY' && (isTextBased(c.type) || c.type === 'FORUM') && !isThread(c.type) && c.name)
      .map((c) => ({ c, sc: score([c.name!], q) }))
      .filter((x) => x.sc > 0)
      .sort((a, b) => b.sc - a.sc || a.c.position - b.c.position)
      .slice(0, LIMIT)
      .map(({ c }) => ({ kind: 'channel' as const, key: `c${c.id}`, id: c.id, label: c.name!, channel: c, insert: `#${c.name}`, raw: `<#${c.id}>` }));
  }
  // ':' Emojis – eigene zuerst, dann Unicode.
  const custom: Suggestion[] = src.customEmojis
    .filter((e) => e.available)
    .map((e) => ({ e, sc: score([e.name], q) }))
    .filter((x) => x.sc > 0)
    .sort((a, b) => b.sc - a.sc)
    .slice(0, LIMIT)
    .map(({ e }) => ({
      kind: 'emoji' as const,
      key: `e${e.id}`,
      label: `:${e.name}:`,
      emoji: { id: e.id, name: e.name, animated: e.animated, url: e.url },
      insert: `:${e.name}:`,
      raw: `<${e.animated ? 'a' : ''}:${e.name}:${e.id}>`,
    }));
  const uni: Suggestion[] = src.unicode(q, LIMIT).map((u) => ({
    kind: 'emoji' as const,
    key: `x${u.unicode}`,
    label: `:${u.shortcodes[0] ?? u.label}:`,
    emoji: { id: null, name: u.unicode, animated: false },
    insert: u.unicode,
    raw: u.unicode,
  }));
  return [...custom, ...uni].slice(0, LIMIT);
}
